// RECALL-BRIEFS: "While you were away", one short card at the top of the Inbox when the person's
// last visit is older than 3 days (design 2026-10-31 §4.1). A line or two per space, three spaces
// at most; what it is made of sits behind (i). Closing it is final for its window. Until memory
// can read for the person the server answers no card, and nothing shows (no placeholder).
import { Button, InfoTip } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { XIcon } from "lucide-react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { featureState, useSession } from "../use-feature";
import { memoryKeys } from "./memory-queries";

export function CatchUpCard({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const qc = useQueryClient();
  const { me } = useSession();
  const enabled =
    featureState(me, co, "memory").enabled &&
    me.memberships.some((m) => m.company_id === co && m.kind === "person");
  const key = [...memoryKeys.all(co), "catch-up"] as const;
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.client.memory.catchUp({ params: { co } }),
    enabled,
    retry: false,
    staleTime: 60_000,
  });
  const close = useMutation({
    mutationFn: (windowKey: string) =>
      api.client.memory.catchUpDismiss({ params: { co }, body: { window_key: windowKey } }),
    onSuccess: (next) => qc.setQueryData(key, next),
  });
  const card = q.data?.card;
  if (!enabled || q.data?.state !== "card" || !card) return null;
  return (
    <section
      data-slot="catch-up-card"
      aria-label="While you were away"
      className="mx-4 mt-3 shrink-0 rounded-md border border-subtle bg-surface px-3 py-2.5"
    >
      <header className="flex items-center gap-1">
        <h2 className="text-sm font-semibold">While you were away</h2>
        <InfoTip label="About this card">{card.info}</InfoTip>
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto size-7"
          aria-label="Close"
          disabled={close.isPending}
          onClick={() => close.mutate(card.window_key)}
        >
          <XIcon aria-hidden="true" className="size-4" />
        </Button>
      </header>
      <ul className="mt-1 space-y-1.5 text-[13px]">
        {card.spaces.map((s) => (
          <li key={s.name}>
            <span className="font-medium">{s.name}</span>
            <ul className="text-secondary">
              {s.lines.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
      {card.more > 0 ? (
        <p className="mt-1 text-xs text-muted-foreground">and {card.more} more</p>
      ) : null}
    </section>
  );
}
